import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  DaySessions,
  getDays,
  getPatient,
  getSessions,
  PatientDetail,
  SessionDay,
  SessionRow,
} from "../../../components/Admin/adminApi";
import { addDays, nyDate, nyTime, timeAgo, useAdminShell } from "../../../components/Admin/adminShell";
import AccountTab from "../../../components/Admin/AccountTab";
import SessionFiles, { filesSummary } from "../../../components/Admin/SessionFiles";
import styles from "./AdminPatient.module.css";

type Tab = "sessions" | "voice" | "account";
const STRIP_DAYS = 14;

function decodeParam(raw: string | undefined): string {
  try {
    return decodeURIComponent(raw || "").trim().toLowerCase();
  } catch {
    return (raw || "").trim().toLowerCase();
  }
}

function score(s: SessionRow): string {
  const values = Array.isArray(s.scores) ? s.scores.map(Number).filter((n) => Number.isFinite(n)) : [];
  if (values.length) return `score ${Math.round(values.reduce((a, b) => a + b, 0) / values.length)}`;
  return "";
}

function SessionList({ email, data }: { email: string; data: DaySessions }) {
  const [open, setOpen] = useState<string | null>(null);
  if (data.sessions.length === 0) return <p className={styles.muted}>No sessions on this day.</p>;
  return (
    <ul className={styles.sessions}>
      {data.sessions.map((s) => {
        const isOpen = open === s.sessionId;
        const missing = s.files.expected != null && s.files.present < s.files.expected;
        return (
          <li key={s.sessionId} className={styles.session}>
            <button type="button" className={styles.sessionHead} aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : s.sessionId)}>
              <span className={styles.time}>{nyTime(s.timestamp)}</span>
              <span className={styles.exercise}>{s.exercise || "Unknown exercise"}</span>
              <span>{s.hand || "—"}</span>
              <span>{s.reps != null ? `${s.reps} reps` : "reps unknown"}</span>
              <span>
                {[score(s), s.avgJerk != null ? `jerk ${s.avgJerk.toFixed(2)}` : ""].filter(Boolean).join(" · ") || "—"}
              </span>
              <span className={missing || s.files.noneReceived ? styles.warn : styles.muted}>files {filesSummary(s.files)}</span>
            </button>
            <SessionFiles files={s.files} device={s.device} sessionId={s.sessionId} email={email} exercise={s.exercise} timestamp={s.timestamp} expanded={isOpen} />
          </li>
        );
      })}
    </ul>
  );
}

/** One patient for admins: header facts, sessions by day with their files, and account actions. */
export default function AdminPatient() {
  const params = useParams();
  const email = decodeParam(params.email);
  const { refreshKey } = useAdminShell();
  const [tab, setTab] = useState<Tab>("sessions");
  const [patient, setPatient] = useState<PatientDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [date, setDate] = useState<string | null>(null);
  const [exercise, setExercise] = useState("");
  const [days, setDays] = useState<SessionDay[]>([]);
  const [day, setDay] = useState<DaySessions | null>(null);
  const [exercises, setExercises] = useState<string[]>([]);
  const today = nyDate(new Date());

  useEffect(() => {
    let live = true;
    getPatient(email)
      .then((p) => {
        if (!live) return;
        setPatient(p);
        setDate((d) => d || (p.lastActive ? nyDate(p.lastActive) : today));
      })
      .catch((e) => live && setError((e as Error).message));
    return () => {
      live = false;
    };
  }, [email, refreshKey, today]);

  // The strip ends today, or at the chosen day if that is older.
  const stripEnd = date && date < addDays(today, -(STRIP_DAYS - 1)) ? addDays(date, Math.floor(STRIP_DAYS / 2)) : today;
  const stripStart = addDays(stripEnd, -(STRIP_DAYS - 1));

  useEffect(() => {
    let live = true;
    getDays(email, stripStart, stripEnd)
      .then((d) => live && setDays(d))
      .catch(() => live && setDays([]));
    return () => {
      live = false;
    };
  }, [email, stripStart, stripEnd, refreshKey]);

  useEffect(() => {
    if (!date) return;
    let live = true;
    setDay(null);
    getSessions(email, date, exercise || undefined)
      .then((d) => {
        if (!live) return;
        setDay(d);
        if (!exercise) setExercises(Array.from(new Set(d.sessions.map((s) => s.exercise || "").filter(Boolean))).sort());
      })
      .catch((e) => live && setError((e as Error).message));
    return () => {
      live = false;
    };
  }, [email, date, exercise, refreshKey]);

  const strip = useMemo(() => {
    const counts = new Map(days.map((d) => [d.date, d.count]));
    return Array.from({ length: STRIP_DAYS }, (_, i) => {
      const ymd = addDays(stripStart, i);
      return { ymd, count: counts.get(ymd) || 0 };
    });
  }, [days, stripStart]);

  const pickDate = (ymd: string) => {
    setExercise("");
    setDate(ymd);
  };

  if (error && !patient) {
    return (
      <p className="ui-banner ui-banner--error" role="alert">
        {error}
      </p>
    );
  }
  if (!patient) {
    return (
      <p>
        <span className="ui-spinner" aria-hidden="true" /> Loading…
      </p>
    );
  }

  const name = [patient.profile.givenName, patient.profile.familyName].filter(Boolean).join(" ");
  const device = patient.latestDevice;

  return (
    <div className={styles.page}>
      <Link to="/admin" className={styles.back}>
        ← Overview
      </Link>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>{patient.email}</h1>
          {name && <p className={styles.muted}>{name}</p>}
          <div className={styles.tags}>
            {patient.tags.map((t) => (
              <span key={t} className="ui-badge ui-badge--neutral">
                {t}
              </span>
            ))}
            {patient.isTest && <span className="ui-badge ui-badge--warning">Test account</span>}
          </div>
        </div>
        <dl className={styles.facts}>
          <dt>App</dt>
          <dd>{device ? [device.appVersion, device.modelName].filter(Boolean).join(" · ") || "—" : "—"}</dd>
          <dt>Last active</dt>
          <dd title={patient.lastActive || undefined}>{patient.lastActive ? timeAgo(patient.lastActive) : "Never"}</dd>
          <dt>Sessions</dt>
          <dd>
            {patient.sessionsThisWeek} this week · {patient.totalSessions} total
          </dd>
        </dl>
      </header>

      <div className="ui-segmented" role="tablist" aria-label="Patient sections">
        <button type="button" role="tab" aria-selected={tab === "sessions"} onClick={() => setTab("sessions")}>
          Sessions
        </button>
        <button type="button" role="tab" aria-selected={false} disabled title="Coming in Phase 4">
          Voice
        </button>
        <button type="button" role="tab" aria-selected={tab === "account"} onClick={() => setTab("account")}>
          Account
        </button>
      </div>

      {error && (
        <p className="ui-banner ui-banner--error" role="alert">
          {error}
        </p>
      )}

      {tab === "account" && <AccountTab patient={patient} onChange={setPatient} />}

      {tab === "sessions" && (
        <>
          <div className={styles.strip} role="group" aria-label="Sessions per day (New York time)">
            {strip.map((d) => (
              <button
                key={d.ymd}
                type="button"
                className={d.ymd === date ? styles.dayActive : d.count ? styles.dayBusy : styles.day}
                aria-pressed={d.ymd === date}
                onClick={() => pickDate(d.ymd)}
              >
                <span className={styles.dayName}>
                  {new Date(`${d.ymd}T12:00:00Z`).toLocaleDateString(undefined, { weekday: "short", timeZone: "UTC" })}
                </span>
                <span>{Number(d.ymd.slice(8))}</span>
                <span className={styles.dayCount}>{d.count || "·"}</span>
              </button>
            ))}
          </div>

          <div className={styles.controls}>
            <label className={styles.control}>
              <span className="ui-label">Day</span>
              <input type="date" className="ui-input" value={date || ""} max={today} onChange={(e) => e.target.value && pickDate(e.target.value)} />
            </label>
            <label className={styles.control}>
              <span className="ui-label">Exercise</span>
              <select className="ui-input" value={exercise} onChange={(e) => setExercise(e.target.value)}>
                <option value="">All exercises</option>
                {exercises.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="ui-card">
            {!day ? (
              <p>
                <span className="ui-spinner" aria-hidden="true" /> Loading…
              </p>
            ) : (
              <>
                <p className={styles.summary}>
                  {`${day.summary.sessions} sessions · ${day.summary.exercises} exercises · ${day.summary.reps} reps · ${day.summary.minutes} min`}
                </p>
                <SessionList email={patient.email} data={day} />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
